import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { getEmptySuggestions } from '../../interfaces/Suggestion/Suggestions';
import { getSuggestions } from '../../api/Suggestion';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ProductGrid } from '../../components/ProductGrid/ProductGrid';
import { CategoryCard } from '../../components/Category/CategoryCard';
import { useThemeColor } from '../../hooks/useThemeColor';
import { GenericHelmet } from '../../components/Helmet/GenericHelmet';
import { useLocalizedText } from '../../hooks/useLocalizedText';

export const SearchResultPage = () => {
  const t = useLocalizedText();

  const { searchValue } = useParams<string>();

  const [suggestions, setSuggestions] = useState(getEmptySuggestions());
  const [loading, setLoading] = useState(false);

  const { primary } = useThemeColor();

  const isMobile = useIsMobile();

  useEffect(() => {
    if (searchValue) {
      setLoading(true);
      getSuggestions(searchValue)
        .then(setSuggestions)
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  }, [searchValue]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
        gap: 5,
      }}
    >
      <GenericHelmet title={t('search.search_result')} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: 4.5,
          alignItems: 'center',
          boxSizing: 'border-box',
          px: isMobile ? 1.5 : 5,
          py: 2.5,
        }}
      >
        <ProductGrid
          title={t('search.products')}
          products={suggestions.products}
          loading={loading}
        />

        {!loading && !suggestions.products.length && (
          <Typography sx={{ mt: -4 }}>{t('search.no_products')}</Typography>
        )}

        {suggestions.categories.length > 0 && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Typography
                fontSize={{ sx: '1.75rem', sm: '36px' }}
                fontWeight={600}
              >
                {t('search.categories')}
              </Typography>

              <Box
                sx={{ backgroundColor: primary, height: '3px', width: '100px' }}
              />
            </Box>

            <Grid container spacing={{ xs: 3 }} width='100%'>
              {suggestions.categories.map((category) => (
                <Grid
                  key={category.slug}
                  size={{
                    xs: 6,
                    sm: 4,
                    md: 3,
                  }}
                >
                  <CategoryCard category={category} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
};
